import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EMAIL_TEMPLATES } from "@/constants/testIds";
import {
  renderTemplate,
  TEMPLATE_VARIABLES,
  TEMPLATE_PREVIEW_SAMPLES,
  openMailto,
} from "@/lib/emailTemplates";
import { toast } from "@/lib/notify";
import RecipientPromptModal from "@/components/RecipientPromptModal";
import {
  FileText,
  RotateCcw,
  Eye,
  Send,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { Modal } from "./UsersAdminModals";

export const TEMPLATE_KEYS = [
  "reminder_48h",
  "request_decision",
  "request_new",
  "monthly_summary",
];

export const TEMPLATE_TAB_LABEL = {
  reminder_48h: "Recordatorio 48h",
  request_decision: "Decisión solicitud",
  request_new: "Nueva solicitud",
  monthly_summary: "Resumen mensual",
};

export default function EmailTemplatesSection() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "administrador";

  const [active, setActive] = useState("reminder_48h");
  const [templates, setTemplates] = useState({}); // key -> full doc
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [testEmailModal, setTestEmailModal] = useState(null); // { defaultTo, samples, subject, body } | null

  // Asistente de variables: detecta {placeholders} en asunto+cuerpo y los
  // clasifica en válidos (usados), desconocidos (posibles typos) y sin usar.
  const assistant = useMemo(() => {
    const valid = new Set(TEMPLATE_VARIABLES[active] || []);
    const found = new Set();
    const re = /\{(\w+)\}/g;
    const text = `${subject}\n${body}`;
    let m;
    while ((m = re.exec(text)) !== null) found.add(m[1]);
    const used = [...found].filter((v) => valid.has(v)).sort();
    const unknown = [...found].filter((v) => !valid.has(v)).sort();
    const unused = [...valid].filter((v) => !found.has(v)).sort();
    return { used, unknown, unused };
  }, [active, subject, body]);

  const load = async () => {
    try {
      const { data } = await api.get("/email-templates");
      const map = {};
      (data || []).forEach((d) => {
        map[d.key] = d;
      });
      setTemplates(map);
      const cur = map[active];
      if (cur) {
        setSubject(cur.subject || "");
        setBody(cur.body || "");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando plantillas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(load);
  }, []);

  const switchTab = (key) => {
    setActive(key);
    setInfo("");
    setError("");
    const cur = templates[key];
    if (cur) {
      setSubject(cur.subject || "");
      setBody(cur.body || "");
    }
  };

  const flash = (msg) => {
    setInfo(msg);
    setError("");
    setTimeout(() => setInfo(""), 2500);
  };

  const save = async () => {
    if (!isAdmin) return;
    setError("");
    if (!subject.trim()) {
      setError("El asunto no puede estar vacío");
      return;
    }
    if (!body.trim()) {
      setError("El cuerpo no puede estar vacío");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/email-templates/${active}`, {
        subject,
        body,
      });
      setTemplates((prev) => ({ ...prev, [active]: data }));
      flash("Plantilla guardada");
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = async () => {
    if (!isAdmin) return;
    setError("");
    try {
      const { data } = await api.post(`/email-templates/${active}/reset`);
      setTemplates((prev) => ({ ...prev, [active]: data }));
      setSubject(data.subject || "");
      setBody(data.body || "");
      flash("Plantilla restaurada al valor por defecto");
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al restaurar");
    }
  };

  const insertVariable = (name) => {
    setBody((b) => `${b}{${name}}`);
  };

  const openPreview = () => {
    const samples = TEMPLATE_PREVIEW_SAMPLES[active] || {};
    setPreview({
      subject: renderTemplate(subject, samples),
      body: renderTemplate(body, samples),
    });
  };

  const sendTestEmail = () => {
    const samples = TEMPLATE_PREVIEW_SAMPLES[active] || {};
    const renderedSubject = renderTemplate(subject, samples);
    const renderedBody = renderTemplate(body, samples);
    setTestEmailModal({
      defaultTo: me?.email || "",
      subject: renderedSubject,
      body: renderedBody,
    });
  };

  const submitTestEmail = (to) => {
    if (!to.includes("@")) {
      toast.error("Correo inválido");
      return;
    }
    const { subject: renderedSubject, body: renderedBody } = testEmailModal;
    // Log the test send (fire-and-forget) so it appears in the Mailto history.
    api
      .post("/mailto-logs", {
        template_key: active,
        recipient: to,
        subject: renderedSubject,
        body_preview: renderedBody.slice(0, 500),
        context_label: "Prueba de plantilla (datos de ejemplo)",
      })
      .catch(() => {});
    openMailto(to, renderedSubject, renderedBody);
    setTestEmailModal(null);
    flash("Email de prueba abierto en tu cliente de correo");
  };

  if (!isAdmin) return null;
  if (loading) return null;

  const current = templates[active];
  const dirty =
    current && (subject !== current.subject || body !== current.body);
  const vars = TEMPLATE_VARIABLES[active] || [];

  return (
    <section
      data-testid={EMAIL_TEMPLATES.section}
      className="mt-10 border border-[#d9cfb8] bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 text-[#1c1a14]">
            <FileText size={16} className="text-[#7a5b0f]" />
            <h3 className="font-serif text-base">
              Plantillas de Email (mailto:)
            </h3>
          </div>
          <p className="text-[11px] text-[#5d5849] mt-0.5">
            Edita el asunto y cuerpo de los emails que el sistema abre en tu
            cliente de correo. Usa variables entre llaves, p. ej.{" "}
            <code className="bg-[#fbf7ee] px-1">{"{titulo}"}</code>.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-[#e8e1cd] mb-4">
        {TEMPLATE_KEYS.map((k) => (
          <button
            key={k}
            type="button"
            data-testid={EMAIL_TEMPLATES.tab(k)}
            onClick={() => switchTab(k)}
            className={`px-3 py-2 text-[11px] uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              active === k
                ? "border-[#c8a04a] text-[#1c1a14]"
                : "border-transparent text-[#8b7e5e] hover:text-[#1c1a14]"
            }`}
          >
            {TEMPLATE_TAB_LABEL[k]}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        {/* Editor */}
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
              Asunto
            </label>
            <input
              data-testid={EMAIL_TEMPLATES.subjectInput}
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={300}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
              Cuerpo
            </label>
            <textarea
              data-testid={EMAIL_TEMPLATES.bodyInput}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              maxLength={8000}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded font-mono focus:border-[#c8a04a] focus:outline-none whitespace-pre-wrap"
            />
            <div className="mt-1 text-[10px] text-[#8b7e5e]">
              {body.length} / 8000 caracteres
            </div>
          </div>

          {/* Asistente de variables */}
          <div
            data-testid={EMAIL_TEMPLATES.assistant}
            className={`rounded border px-3 py-2 text-[11px] ${
              assistant.unknown.length > 0
                ? "border-amber-300 bg-amber-50"
                : "border-[#e8e1cd] bg-[#fbf7ee]"
            }`}
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#5b5240] mb-1.5">
              <Sparkles size={12} className="text-[#7a5b0f]" />
              Asistente de variables
            </div>

            {assistant.unknown.length > 0 && (
              <div className="mb-2">
                <div className="flex items-center gap-1 text-amber-800 font-medium mb-1">
                  <AlertTriangle size={12} />
                  Variables desconocidas (posible typo):
                </div>
                <div className="flex flex-wrap gap-1">
                  {assistant.unknown.map((v) => (
                    <span
                      key={v}
                      data-testid={EMAIL_TEMPLATES.assistantUnknownChip(v)}
                      className="inline-flex items-center px-2 py-0.5 rounded border border-amber-400 bg-amber-100 text-amber-900 font-mono"
                      title="Esta variable no será sustituida — revisa la ortografía"
                    >
                      {`{${v}}`}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <div className="text-[#5d5849] mb-1">
                  Variables usadas{" "}
                  <span className="text-[#8b7e5e]">
                    ({assistant.used.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {assistant.used.length === 0 ? (
                    <span className="text-[#8b7e5e] italic">
                      (ninguna aún)
                    </span>
                  ) : (
                    assistant.used.map((v) => (
                      <span
                        key={v}
                        data-testid={EMAIL_TEMPLATES.assistantUsedChip(v)}
                        className="inline-flex items-center px-2 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 font-mono"
                      >
                        {`{${v}}`}
                      </span>
                    ))
                  )}
                </div>
              </div>
              <div>
                <div className="text-[#5d5849] mb-1">
                  Sin usar{" "}
                  <span className="text-[#8b7e5e]">
                    ({assistant.unused.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {assistant.unused.length === 0 ? (
                    <span className="text-[#8b7e5e] italic">
                      (todas usadas)
                    </span>
                  ) : (
                    assistant.unused.map((v) => (
                      <span
                        key={v}
                        data-testid={EMAIL_TEMPLATES.assistantUnusedChip(v)}
                        className="inline-flex items-center px-2 py-0.5 rounded border border-[#d9cfb8] bg-white text-[#5d5849] font-mono"
                      >
                        {`{${v}}`}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div
              data-testid={EMAIL_TEMPLATES.errorMsg}
              className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700"
            >
              <AlertCircle size={14} /> {error}
            </div>
          )}
          {info && !error && (
            <div
              data-testid={EMAIL_TEMPLATES.infoMsg}
              className="flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700"
            >
              <CheckCircle2 size={14} /> {info}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={EMAIL_TEMPLATES.resetButton}
                onClick={resetDefault}
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#5b5240] hover:text-[#1c1a14]"
              >
                <RotateCcw size={12} /> Restaurar por defecto
              </button>
              <button
                type="button"
                data-testid={EMAIL_TEMPLATES.previewButton}
                onClick={openPreview}
                className="inline-flex items-center gap-1 rounded border border-[#d9cfb8] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#fbf7ee]"
              >
                <Eye size={12} /> Vista previa
              </button>
              <button
                type="button"
                data-testid={EMAIL_TEMPLATES.testSendButton}
                onClick={sendTestEmail}
                title="Abre tu cliente de email con la plantilla actual (incluye ediciones sin guardar) y datos de ejemplo"
                className="inline-flex items-center gap-1 rounded border border-[#0f7a4a] bg-[#0f7a4a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white hover:bg-[#0a5a35]"
              >
                <Send size={12} /> Probar envío
              </button>
            </div>
            <button
              type="button"
              data-testid={EMAIL_TEMPLATES.saveButton}
              onClick={save}
              disabled={!dirty || saving}
              className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? "Guardando..." : "Guardar plantilla"}
            </button>
          </div>
        </div>

        {/* Variables panel */}
        <aside className="border border-[#e8e1cd] bg-[#fbf7ee] p-3 rounded h-fit">
          <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-2">
            Variables disponibles
          </div>
          <p className="text-[11px] text-[#5d5849] mb-2">
            Haz clic para insertar al final del cuerpo.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {vars.map((name) => (
              <button
                key={name}
                type="button"
                data-testid={EMAIL_TEMPLATES.variableChip(name)}
                onClick={() => insertVariable(name)}
                className="inline-flex items-center px-2 py-1 rounded border border-[#d9cfb8] bg-white text-[11px] font-mono text-[#1c1a14] hover:bg-[#c8a04a] hover:text-white transition-colors"
              >
                {`{${name}}`}
              </button>
            ))}
          </div>
        </aside>
      </div>

      {preview && (
        <Modal
          testid={EMAIL_TEMPLATES.previewModal}
          title="Vista previa del email"
          onClose={() => setPreview(null)}
        >
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
                Asunto
              </div>
              <div
                data-testid={EMAIL_TEMPLATES.previewSubject}
                className="border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              >
                {preview.subject}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
                Cuerpo
              </div>
              <pre
                data-testid={EMAIL_TEMPLATES.previewBody}
                className="border border-[#d9cfb8] bg-[#fbf7ee] px-3 py-2 text-[12px] rounded whitespace-pre-wrap font-mono"
              >
                {preview.body}
              </pre>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                data-testid={EMAIL_TEMPLATES.previewClose}
                onClick={() => setPreview(null)}
                className="rounded border border-[#1c2a3a] bg-[#1c2a3a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white hover:bg-[#0f1a2a]"
              >
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {testEmailModal && (
        <RecipientPromptModal
          testid="email-templates-test-modal"
          title="Enviar correo de prueba"
          subtitle={TEMPLATE_TAB_LABEL[active]}
          defaultValue={testEmailModal.defaultTo}
          placeholder="tu-correo@ajar.com"
          confirmLabel="Enviar prueba"
          onCancel={() => setTestEmailModal(null)}
          onConfirm={submitTestEmail}
        />
      )}
    </section>
  );
}
